import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { isAdminHost, isPlatformBrandHost } from '../shared/utils/host'
import { applyPwaIdentity } from '../shared/utils/pwaIdentity'
import { TENANT_BRANDING_STORAGE_KEY } from '../config/config.js'

const TenantContext = createContext()
const STORAGE_KEY = TENANT_BRANDING_STORAGE_KEY

export const TenantProvider = ({ children }) => {
  const cached = (() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : null
    } catch { return null }
  })()

  const [branding, setBranding] = useState({
    tenantId: cached?.tenantId || null,
    name: cached?.name || 'Portal',
    // Neutral defaults (NOT the Tynvara brand) so a tenant host never flashes the
    // platform brand before its own branding resolves. Platform hosts and configured
    // tenants override these in fetchBranding().
    primaryColor: cached?.primaryColor || '#334155',
    secondaryColor: cached?.secondaryColor || '#475569',
    logoUrl: cached?.logoUrl || '/images/whitelogo-placeholder.svg',
    faviconUrl: cached?.faviconUrl || '/favicon.svg',
    // Per-tenant feature flags (UX module-gating; the server enforces authoritatively).
    features: cached?.features || {},
    // Rung (d) of the language chain: "our crews read Spanish", set once by an admin. It rides on
    // BRANDING because branding is the only thing resolved by HOST before anybody logs in, and the
    // login page is exactly where a company default has to reach — there is no user there to have a
    // preference. null = never set, which is not the same as English (see shared/i18n/languages.js).
    defaultLanguage: cached?.defaultLanguage || null,
    // HOW LONG A SESSION LIVES HERE, in minutes idle and hours absolute. Rides on branding for the
    // same reason the language does: this is the only per-tenant payload resolved by HOST and
    // available at first paint, and a session clock that starts on the platform default and then
    // changes when a fetch lands is a clock nobody can reason about. null = never set; the defaults
    // and the bounds live in shared/api/sessionPolicy.js, which is the only reader.
    sessionIdleMinutes: cached?.sessionIdleMinutes ?? null,
    sessionAbsoluteHours: cached?.sessionAbsoluteHours ?? null,
    loading: !cached,
    error: null
  })

  const fetchBranding = useCallback(async () => {
    try {
        const domain = window.location.hostname
        
        if (isAdminHost(domain)) {
          const adminBranding = {
            tenantId: 'master',
            name: 'Master Admin Portal',
            primaryColor: '#000000',
            secondaryColor: '#333333',
            logoUrl: '/images/tynvara-logo-light.png',
            faviconUrl: '/favicon.svg',
            features: {},
            defaultLanguage: null,
            sessionIdleMinutes: null,
            sessionAbsoluteHours: null,
            loading: false,
            error: null
          }
          setBranding(adminBranding)
          
          document.documentElement.style.setProperty('--primary-color', adminBranding.primaryColor)
          document.documentElement.style.setProperty('--secondary-color', adminBranding.secondaryColor)
          document.documentElement.style.setProperty('--brand-primary', '0 0 0')
          document.documentElement.style.setProperty('--brand-primary-light', '51 51 51')
          document.documentElement.style.setProperty('--brand-primary-dark', '0 0 0')
          document.documentElement.style.setProperty('--brand-accent', '51 51 51')
          document.title = `${adminBranding.name}`
          applyPwaIdentity({
            name: adminBranding.name, shortName: 'Admin',
            themeColor: adminBranding.primaryColor, iconUrl: adminBranding.faviconUrl,
          })
          return
        }

        // The platform's OWN app hosts render the Tynvara brand. Every other host is a
        // tenant host and must NEVER fall back to Tynvara (see neutral defaults below).
        // Match an EXPLICIT platform allowlist (shared/utils/host) — never a loose prefix
        // like startsWith('dev.'), which wrongly hijacked dev.<tenant>.com to the Tynvara brand.
        if (isPlatformBrandHost(domain)) {
          const platformBranding = {
            tenantId: null,
            name: 'Tynvara',
            primaryColor: '#1D4ED8',
            secondaryColor: '#3B82F6',
            logoUrl: '/images/tynvara-logo-light.png',
            faviconUrl: '/favicon.svg',
            features: {},
            defaultLanguage: null,
            loading: false,
            error: null
          }
          setBranding(platformBranding)
          document.documentElement.style.setProperty('--primary-color', platformBranding.primaryColor)
          document.documentElement.style.setProperty('--secondary-color', platformBranding.secondaryColor)
          document.documentElement.style.setProperty('--brand-primary', '29 78 216')
          document.documentElement.style.setProperty('--brand-primary-light', '59 130 246')
          document.documentElement.style.setProperty('--brand-primary-dark', '30 58 138')
          document.documentElement.style.setProperty('--brand-accent', '59 130 246')
          document.title = platformBranding.name
          applyPwaIdentity({
            name: platformBranding.name, shortName: platformBranding.name,
            themeColor: platformBranding.primaryColor, iconUrl: platformBranding.faviconUrl,
          })
          return
        }

        const response = await fetch(`/api/identity/branding?domain=${domain}`)
        
        if (response.ok) {
          const data = await response.json()
          
          const newBranding = {
            tenantId: data.tenant_id || null,
            name: data.name || 'Portal',
            // Unconfigured tenant → NEUTRAL, never the Tynvara platform brand.
            primaryColor: data.primary_color || '#334155',
            secondaryColor: data.secondary_color || '#475569',
            // Prefer the tenant's uploaded logo (app_logos, host-resolved) so the Brand editor
            // actually controls the login/header logo. The endpoint serves a NEUTRAL placeholder
            // when the tenant has none configured — never another brand.
            logoUrl: data.logo_url || `/api/identity/logo/dark?domain=${encodeURIComponent(domain)}`,
            // THE APP ICON, from the slot the Brand editor now offers ('icon' in app_logos).
            //
            // NOT a fallback to `logo_url`, which was the first thing I reached for and is wrong:
            // the login/header logo is a WHITE logo by design — it exists to sit on a dark header —
            // so as a tab icon on a white tab strip it is invisible. Swapping a wrong-brand "B" for
            // a blank square is not an improvement.
            //
            // `favicon_url` stays first because it is an existing settings field some tenant may
            // already have set through the API; the uploaded icon is the path anyone can actually
            // use; and the neutral brand-free mark is last. Never another tenant's, and never the
            // platform's on a tenant host.
            faviconUrl: data.favicon_url
              || (data.has_icon ? `/api/identity/logo/icon?domain=${encodeURIComponent(domain)}` : null)
              || '/favicon.svg',
            features: data.features || {},
            // null rather than 'en' on an unset tenant: "never chosen" has to stay distinguishable
            // from "chose English", or the device guess below it can never fire.
            defaultLanguage: data.default_language || null,
            // See the note on the initial state above. `?? null` rather than `|| null` so a tenant
            // is not silently prevented from configuring a zero — the bounds check that refuses one
            // belongs in sessionPolicy.js, not in a truthiness test here.
            sessionIdleMinutes: data.session_idle_minutes ?? null,
            sessionAbsoluteHours: data.session_absolute_hours ?? null,
            loading: false,
            error: null
          }
          setBranding(newBranding)

          // Persist to localStorage so the NEXT load paints this tenant's name, colours and logo
          // on the first frame instead of the neutral placeholder — that is the whole job of this
          // write (see `loading: !cached` in the initial state above).
          try {
            const { loading, error, ...toStore } = newBranding
            localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore))
          } catch {
            // SWALLOWED, and here is what it costs: the anti-flash cache, and nothing else.
            // `setItem` is what realistically throws here — a full quota, or a private window
            // refusing storage outright — though `JSON.stringify` shares this try and would throw on
            // a circular value too. Either way `branding` was ALREADY set from the live response
            // above, so THIS render is correct whether or not the write lands.
            //
            // Safe because the cache is never authoritative in either direction. The reader at the
            // top of this file returns null on any failure, and fetchBranding() runs unconditionally
            // from the mount effect below, so the cache is a HEAD START and never a substitute: a
            // browser that can never write it just shows the neutral defaults for the length of one
            // /api/identity/branding round-trip, every load.
            //
            // Not surfaced, because there is nowhere to surface it: this runs before the app has
            // painted anything, and "your browser would not save the colour scheme" is not
            // something the person opening a login page can act on.
          }

          // Convert hex to rgb channels for Tailwind opacity support
          const hexToRgbChannels = (hex) => {
            let c = (hex || '').replace('#', '');
            if (c.length === 3) c = c.split('').map(x => x + x).join('');
            const num = parseInt(c || '000000', 16);
            return `${num >> 16} ${(num >> 8) & 255} ${num & 255}`;
          };

          const adjustHexToChannels = (hex, factor) => {
            let c = (hex || '').replace('#', '');
            if (c.length === 3) c = c.split('').map(x => x + x).join('');
            const num = parseInt(c || '000000', 16);
            let r = Math.min(255, Math.max(0, Math.round((num >> 16) * factor)));
            let g = Math.min(255, Math.max(0, Math.round(((num >> 8) & 255) * factor)));
            let b = Math.min(255, Math.max(0, Math.round((num & 255) * factor)));
            return `${r} ${g} ${b}`;
          };

          // Update CSS variables
          document.documentElement.style.setProperty('--primary-color', newBranding.primaryColor)
          document.documentElement.style.setProperty('--secondary-color', newBranding.secondaryColor)
          try {
            document.documentElement.style.setProperty('--brand-primary', hexToRgbChannels(newBranding.primaryColor))
            document.documentElement.style.setProperty('--brand-primary-light', adjustHexToChannels(newBranding.primaryColor, 1.3))
            document.documentElement.style.setProperty('--brand-primary-dark', adjustHexToChannels(newBranding.primaryColor, 0.7))
            document.documentElement.style.setProperty('--brand-accent', hexToRgbChannels(newBranding.secondaryColor))
          } catch (e) { console.warn('Color conversion failed', e) }
          
          // THE FAVICON IS SET BY applyPwaIdentity BELOW, not here. What used to sit at this line
          // looked like it worked and did not:
          //
          //   * it selected `link[rel*='icon']` — a SUBSTRING match, so it could just as easily
          //     grab the apple-touch-icon and repurpose it, leaving the tab untouched;
          //   * it then asserted `type = 'image/x-icon'` over a tenant logo that is a PNG or an
          //     SVG, so the declared type contradicted the bytes;
          //   * and it appended the element again, which for an element already in <head> is a
          //     move rather than an add — reordering the icon links as a side effect.
          //
          // One place now owns tab icon, iOS home-screen icon and install manifest, so they cannot
          // drift apart again. See pwaIdentity.js.

          // Update title + per-tenant PWA install identity (so this tenant installs under its own
          // name/icon, not a shared default). Uses the resolved branding.
          document.title = `${data.name} - Portal`
          applyPwaIdentity({
            name: newBranding.name,
            shortName: newBranding.name,
            themeColor: newBranding.primaryColor,
            iconUrl: newBranding.faviconUrl,
          })
        } else {
          setBranding(prev => ({ ...prev, loading: false }))
        }
      } catch (err) {
        console.error('Failed to fetch tenant branding:', err)
        setBranding(prev => ({ ...prev, loading: false, error: err.message }))
      }
  }, [])

  useEffect(() => {
    fetchBranding()
  }, [fetchBranding])

  const contextValue = {
    ...branding,
    refresh: fetchBranding
  }

  return (
    <TenantContext.Provider value={contextValue}>
      {children}
    </TenantContext.Provider>
  )
}

export const useTenantBranding = () => {
  return useContext(TenantContext)
}

// UX-only per-tenant feature gate (the server is authoritative). True iff the current tenant has
// the flag enabled in features_enabled. Safe on platform/admin hosts (features = {}).
export const useTenantFeature = (key) => {
  const ctx = useContext(TenantContext)
  return Boolean(ctx?.features?.[key])
}
